import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { auth as firebaseAuth } from "@/firebase";
import { onAuthStateChanged, signOut as firebaseSignOut, getRedirectResult } from "firebase/auth";
import { initGA, trackPageView } from "@/lib/ga";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import VerifyEmailPromptPage from "@/pages/VerifyEmailPromptPage";
import Dashboard from "@/pages/Dashboard";
import PredictionsPage from "@/pages/PredictionsPage";
import TicketsPage from "@/pages/TicketsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import AdminPage from "@/pages/AdminPage";
import SettingsPage from "@/pages/SettingsPage";
import WinningsPage from "@/pages/WinningsPage";
import ReportsPage from "@/pages/ReportsPage";
import ContactPage from "@/pages/ContactPage";
import EarnPage from "@/pages/EarnPage";
import AdminBooksPage from "@/pages/AdminBooksPage";
import CommunityStatsPage from "@/pages/CommunityStatsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Auth storage helpers ----------------------------------------------------
// Honours the user's "Keep me logged in" preference:
//   • Persistent (checked, default): writes go to localStorage → survives browser close
//   • Session-only (unchecked): writes go to sessionStorage → cleared when tab/browser closes
// Reads always check sessionStorage first (active session wins), then localStorage.
// The "aura_keep_logged_in" flag itself lives in localStorage so we remember the
// user's choice across sessions for the same browser.
const KEEP_KEY = "aura_keep_logged_in";

const isKeepLoggedIn = () => {
  try { return localStorage.getItem(KEEP_KEY) !== "false"; } catch { return true; }
};

export const setKeepLoggedIn = (keep) => {
  try { localStorage.setItem(KEEP_KEY, keep ? "true" : "false"); } catch { /* noop */ }
};

export const authStorageGet = (key) => {
  try {
    const s = sessionStorage.getItem(key);
    if (s !== null) return s;
    return localStorage.getItem(key);
  } catch { return null; }
};

export const authStorageSet = (key, value) => {
  const persist = isKeepLoggedIn();
  try {
    if (persist) {
      localStorage.setItem(key, value);
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
      localStorage.removeItem(key);
    }
  } catch { /* noop */ }
};

export const authStorageRemove = (key) => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
  try { sessionStorage.removeItem(key); } catch { /* noop */ }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = authStorageGet("aura_user");
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(authStorageGet("aura_token"));
  const exchangingRef = useRef(false);

  // Exchange Firebase ID token for backend JWT
  const exchangeFirebaseToken = useCallback(async (firebaseUser) => {
    if (exchangingRef.current) return;
    exchangingRef.current = true;
    try {
      console.log('[AURA Auth] Exchanging Firebase token for:', firebaseUser.email);
      const idToken = await firebaseUser.getIdToken(true);
      console.log('[AURA Auth] Got ID token, calling POST /api/auth/firebase...');
      const response = await axios.post(`${API}/auth/firebase`, { id_token: idToken });
      const { access_token, user: userData } = response.data;
      console.log('[AURA Auth] Backend returned JWT for user:', userData.email);
      setToken(access_token);
      authStorageSet("aura_token", access_token);
      authStorageSet("aura_user", JSON.stringify(userData));
      setUser(userData);
      console.log('[AURA Auth] User state set, token stored');
    } catch (err) {
      console.error("[AURA Auth] Firebase token exchange FAILED:", err.response?.status, err.response?.data || err.message);
      setUser(null);
      setToken(null);
      authStorageRemove("aura_token");
      authStorageRemove("aura_user");
    } finally {
      exchangingRef.current = false;
    }
  }, []);

  // Validate existing session on mount
  const checkAuth = useCallback(async () => {
    const storedToken = authStorageGet("aura_token");
    if (!storedToken) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${storedToken}` },
        timeout: 8000,
      });
      setUser(response.data);
      authStorageSet("aura_user", JSON.stringify(response.data));
    } catch {
      // Token invalid — check if Firebase still has a session
      const fbUser = firebaseAuth.currentUser;
      if (fbUser) {
        await exchangeFirebaseToken(fbUser);
      } else {
        setUser(null);
        setToken(null);
        authStorageRemove("aura_token");
        authStorageRemove("aura_user");
      }
    } finally {
      setLoading(false);
    }
  }, [exchangeFirebaseToken]);

  useEffect(() => {
    let mounted = true;
    let unsubscribe = () => {};

    const initAuth = async () => {
      // If we have a cached token+user, validate in background — don't block render
      const cachedToken = authStorageGet("aura_token");
      const cachedUser = authStorageGet("aura_user");
      if (cachedToken && cachedUser) {
        // Immediately unblock UI with cached data
        if (mounted) setLoading(false);
        // Validate in background
        checkAuth();
      }

      // Safety timeout: never leave app in loading state > 5s
      const safetyTimer = setTimeout(() => { if (mounted) setLoading(false); }, 5000);

      // Process any pending Google redirect
      try {
        const redirectResult = await getRedirectResult(firebaseAuth);
        if (redirectResult?.user && mounted) {
          await exchangeFirebaseToken(redirectResult.user);
          if (mounted) setLoading(false);
        }
      } catch (err) {
        if (err.code !== 'auth/popup-closed-by-user') {
          console.error('[AURA Auth] redirect error:', err.code);
        }
      }

      clearTimeout(safetyTimer);
      if (!mounted) return;

      // Set up auth state listener
      unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
        if (!mounted) return;
        if (firebaseUser) {
          const storedToken = authStorageGet("aura_token");
          if (storedToken) {
            await checkAuth();
          } else if (!exchangingRef.current) {
            await exchangeFirebaseToken(firebaseUser);
          }
          if (mounted) setLoading(false);
        } else {
          // Firebase has no signed-in user. DON'T blow away our session — for
          // email/password users we intentionally signOut of Firebase after
          // signup/verify (iter_36), and they live entirely on our backend JWT.
          // Only clear the session if we genuinely have no valid backend user.
          const cachedToken = authStorageGet("aura_token");
          const cachedUser = authStorageGet("aura_user");
          if (!cachedToken || !cachedUser) {
            setUser(null);
            setToken(null);
            authStorageRemove("aura_token");
            authStorageRemove("aura_user");
          }
          if (mounted) setLoading(false);
        }
      });
    };

    initAuth();

    return () => { mounted = false; unsubscribe(); };
  }, [checkAuth, exchangeFirebaseToken]);

  const login = async (email, password) => {
    // Legacy email/password via backend (kept for compatibility)
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    authStorageSet("aura_token", access_token);
    authStorageSet("aura_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    // Legacy register via backend (kept for compatibility)
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    authStorageSet("aura_token", access_token);
    authStorageSet("aura_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await firebaseSignOut(firebaseAuth);
    } catch { /* ignore */ }
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
    setToken(null);
    authStorageRemove("aura_token");
    authStorageRemove("aura_user");
  };

  const refreshUser = async () => {
    // Re-exchange Firebase token for fresh backend data
    const fbUser = firebaseAuth.currentUser;
    if (fbUser) {
      await exchangeFirebaseToken(fbUser);
    } else {
      await checkAuth();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          setUser(response.data);
          authStorageSet("aura_user", JSON.stringify(response.data));
          navigate("/dashboard", { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processCallback();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center">
      <div className="text-center">
        <div className="eight-ball aura-glow mx-auto mb-6" />
        <p className="text-aura-text-secondary">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component - requires authentication only
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="text-center">
          <div className="eight-ball aura-glow mx-auto mb-6" />
          <p className="text-aura-text-secondary animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Verified Route Component - requires authentication AND email verification
const VerifiedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="text-center">
          <div className="eight-ball aura-glow mx-auto mb-6" />
          <p className="text-aura-text-secondary animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check email verification (Google OAuth users are auto-verified)
  const isGoogleAuth = user.auth_provider === "google";
  // For Google users we trust auth_provider. For any other provider, email_verified
  // MUST be explicitly true. Undefined / missing → treat as unverified and block.
  const isEmailVerified = user.email_verified === true;
  
  if (!isGoogleAuth && !isEmailVerified) {
    return <Navigate to="/verify-email-prompt" replace />;
  }

  return children;
};

// Referral Capture — catches unmatched /ref=CODE paths, stores attribution, redirects
const CatchAll = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const match = location.pathname.match(/^\/ref=(.+)$/);
    if (match) {
      localStorage.setItem("aura_referral_code", match[1]);
      localStorage.setItem("aura_referral_ts", Date.now().toString());
    }
    navigate("/", { replace: true });
  }, [location, navigate]);
  return null;
};

// GA4 page-view tracker — fires on every client-side route change.
const GAPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email-prompt" element={
        <ProtectedRoute>
          <VerifyEmailPromptPage />
        </ProtectedRoute>
      } />
      <Route path="/winnings" element={<WinningsPage />} />
      <Route
        path="/dashboard"
        element={
          <VerifiedRoute>
            <Dashboard />
          </VerifiedRoute>
        }
      />
      <Route
        path="/predictions"
        element={
          <VerifiedRoute>
            <PredictionsPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/tickets"
        element={
          <VerifiedRoute>
            <TicketsPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <VerifiedRoute>
            <AnalyticsPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <VerifiedRoute>
            <SubscriptionPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <VerifiedRoute>
            <SubscriptionSuccess />
          </VerifiedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <VerifiedRoute>
            <SettingsPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <VerifiedRoute>
            <ReportsPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <VerifiedRoute>
            <AdminPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/contact"
        element={
          <VerifiedRoute>
            <ContactPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/earn"
        element={
          <VerifiedRoute>
            <EarnPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/admin-books"
        element={
          <VerifiedRoute>
            <AdminBooksPage />
          </VerifiedRoute>
        }
      />
      <Route
        path="/community-stats"
        element={
          <VerifiedRoute>
            <CommunityStatsPage />
          </VerifiedRoute>
        }
      />
      <Route path="*" element={<CatchAll />} />
    </Routes>
  );
};

function App() {
  // Load GA4 once on app boot (no-op if REACT_APP_GA_MEASUREMENT_ID is unset).
  useEffect(() => { initGA(); }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-aura-bg">
          <GAPageTracker />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
